// packages/components/src/support-ai/AssistantResponse.tsx
import React from 'react';
import { Button } from '@dopomogai/ui';
import { CheckCircle2, AlertCircle, BrainCircuit } from 'lucide-react';

// --- Types for this component's props ---
interface ToolUsageStep {
  toolName: string;
  description: string;
  status: 'pending' | 'success' | 'error';
}

interface SuggestedAction {
  text: string;
  actionId: string;
}

interface AssistantResponseProps {
  responseText?: string;
  toolUsage?: ToolUsageStep[];
  suggestedActions?: SuggestedAction[];
  onActionClick?: (actionId: string) => void;
}

// --- Sub-components for better organization ---

const ToolUsageIndicator: React.FC<{ steps: ToolUsageStep[] }> = ({ steps }) => (
  <div className="border-t border-slate-200 mt-3 pt-3">
    <p className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1.5">
      <BrainCircuit size={14} />
      AI Reasoning Steps
    </p>
    <div className="space-y-1.5">
      {steps.map((step, index) => (
        <div key={index} className="flex items-center gap-2 text-xs text-slate-600">
          {step.status === 'success' 
            ? <CheckCircle2 size={14} className="text-emerald-500 flex-shrink-0" /> 
            : <AlertCircle size={14} className="text-amber-500 flex-shrink-0" />}
          <span>{step.description}</span>
        </div>
      ))}
    </div>
  </div>
);

const ActionsBar: React.FC<{ actions: SuggestedAction[], onClick: (id: string) => void }> = ({ actions, onClick }) => (
  <div className="border-t border-slate-200 mt-3 pt-3">
    <div className="flex items-center gap-2 flex-wrap">
      {actions.map(action => (
        <Button
          key={action.actionId}
          text={action.text}
          size="sm"
          variant="outline"
          onClick={() => onClick(action.actionId)}
        />
      ))}
    </div>
  </div>
);

// --- The Main Component ---

export const AssistantResponse: React.FC<AssistantResponseProps> = ({
  responseText,
  toolUsage,
  suggestedActions,
  onActionClick,
}) => {
  // Don't render anything if the AI has nothing to say or do.
  if (!responseText && !toolUsage && !suggestedActions) {
    return null;
  }

  return (
    <div className="flex justify-start">
      {/* This distinct style is for AI-to-Agent "thought" messages */}
      <div className="bg-sky-50 border-2 border-sky-200/80 text-slate-800 rounded-xl rounded-bl-none p-4 max-w-xl shadow-sm w-full">
        {responseText && (
          <div className="text-sm prose prose-slate max-w-none">
             <p className="text-xs font-semibold text-sky-700 mb-2 flex items-center gap-1.5">
                <BrainCircuit size={14} />
                AI Analysis
             </p>
            <p className="italic">{responseText}</p>
          </div>
        )}

        {toolUsage && toolUsage.length > 0 && (
          <ToolUsageIndicator steps={toolUsage} />
        )}

        {suggestedActions && suggestedActions.length > 0 && onActionClick && (
          <ActionsBar actions={suggestedActions} onClick={onActionClick} />
        )}
      </div>
    </div>
  );
};